<script setup lang="ts">
import { pinia } from '@/stores'
import { useRouterStore } from '@/stores/router'
import { onMounted, onUnmounted, ref, computed } from 'vue'
import AppButton from '../ui/AppButton.vue'
import Counter from '../ui/Counter.vue'
import { cleanQueryString } from '@/utils/router'
import { getStore, removeStore } from '@/utils/remote-diagnose'
import { getLimitDate, removeLimitDate } from '@/utils/limit-date'
import { useLang } from '@/composables/use-lang'
import PaymentResultContent from '@/lang/payment-result.json'
import { validatePaymentStatus } from '@/services/remote-diagnose'

// Payment status enum to standardize status values
enum PaymentStatus {
  PENDING = 'pending',
  SUCCESS = 'success',
  FAILED = 'failed',
  TIMEOUT = 'timeout',
  CANCELLED = 'cancelled'
}

const props = defineProps({
  aws_url: {
    type: String,
    required: true
  }
})

const { push, current } = useRouterStore(pinia)

const emit = defineEmits(['scroll'])

const langContent = useLang(PaymentResultContent)

const has_dongle = ref<boolean>(false)
const error = ref<boolean>(false)
const isVerifying = ref<boolean>(false)
const paymentStatus = ref<PaymentStatus>(PaymentStatus.PENDING)
const countdown = ref<number>(120) // 2 minutes in seconds
const reference = ref<string>('')
const shouldValidatePayment = ref<boolean>(false)

let countdownInterval: ReturnType<typeof setInterval> | null = null
let verificationInterval: ReturnType<typeof setInterval> | null = null

const formatTime = computed(() => {
  const minutes = Math.floor(countdown.value / 60)
  const seconds = countdown.value % 60
  return `${minutes}:${seconds.toString().padStart(2, '0')}`
})

const handleNext = () => {
  push('payment')
}

const startCountdown = () => {
  countdownInterval = setInterval(() => {
    countdown.value--
    if (countdown.value <= 0) {
      clearInterval(countdownInterval!)
      clearInterval(verificationInterval!)
      if (paymentStatus.value === PaymentStatus.PENDING) {
        paymentStatus.value = PaymentStatus.TIMEOUT
        error.value = true
      }
    }
  }, 1000)
}

const checkPaymentStatus = async () => {
  if (!reference.value) return
  
  try {
    const result = await validatePaymentStatus(reference.value, props.aws_url)

    reference.value = result.reference
    
    if (result.status === PaymentStatus.SUCCESS) {
      paymentStatus.value = PaymentStatus.SUCCESS
      isVerifying.value = false
      clearInterval(countdownInterval!)
      clearInterval(verificationInterval!)
      
      // Handle successful payment - get user data
      const user = getStore()
      if (user) {
        has_dongle.value = user.has_dongle
        removeStore()
      }
      
      if (getLimitDate()) {
        removeLimitDate()
      }
    } else if (result.status === PaymentStatus.FAILED) {
      paymentStatus.value = PaymentStatus.FAILED
      error.value = true
      isVerifying.value = false
      clearInterval(countdownInterval!)
      clearInterval(verificationInterval!)
    }
    // If status is still 'pending', continue polling
  } catch (err) {
    console.error('Error checking payment status:', err)
  }
}

const startVerification = () => {
  isVerifying.value = true
  checkPaymentStatus() // Check immediately
  verificationInterval = setInterval(checkPaymentStatus, 20000) // Check every 20 seconds
}

onMounted(async () => {
  emit('scroll')
  cleanQueryString()

  reference.value = current.params.reference || ''
  shouldValidatePayment.value = current.params.validate_payment === 'true'
  
  // Handle immediate failure cases
  if (current.params.redirect_status === 'failed' || shouldValidatePayment.value && !current.params.reference) {
    error.value = true
    paymentStatus.value = PaymentStatus.FAILED
    return
  }
  
  // Only start the verification process if validate_payment is true
  if (shouldValidatePayment.value) {
    startCountdown()
    startVerification()
  } else {
    // If not validating payment, show success directly
    paymentStatus.value = PaymentStatus.SUCCESS
    
    // Handle successful payment - get user data
    const user = getStore()
    if (user) {
      has_dongle.value = user.has_dongle
      removeStore()
    }
    
    if (getLimitDate()) {
      removeLimitDate()
    }
  }
})

onUnmounted(() => {
  if (countdownInterval) clearInterval(countdownInterval)
  if (verificationInterval) clearInterval(verificationInterval)
})
</script>

<template>
  <div v-if="langContent" class="text-center">
    <!-- Payment Verification State -->
    <div v-if="shouldValidatePayment && isVerifying && paymentStatus === PaymentStatus.PENDING">
      <h2 class="mb-10">{{ langContent.verifying_payment }}</h2>
      <p class="mb-6">{{ langContent.verification_message }}</p>
      <div class="mb-6">
        <p class="text-2xl font-bold text-english-vermillion">{{ formatTime }}</p>
        <p class="text-sm text-gray-600 mt-2">Time remaining</p>
      </div>
    </div>

    <!-- Payment Success State -->
    <div v-else-if="paymentStatus === PaymentStatus.SUCCESS && !error">
      <h2 class="mb-10">{{ langContent.payment_success }}</h2>
      <p>{{ langContent.thank_you_payment }}</p>
      <p v-if="has_dongle">{{ langContent.whatsapp_instruction }}</p>
      <p v-else>{{ langContent.dongle_request }}</p>
      <p class="mt-10 mb-5">{{ langContent.diagnostic_info_part_one }}</p>
      <p class="mb-10">{{ langContent.diagnostic_info_part_two }}</p>
      <p>{{ langContent.contact_us }}</p>
    </div>

    <!-- Payment Error States -->
    <div v-else-if="error">
      <!-- Timeout Error -->
      <div v-if="paymentStatus === PaymentStatus.TIMEOUT">
        <h2 class="mb-10">{{ langContent.payment_timeout }}</h2>
        <p class="mb-6">{{ langContent.timeout_message }}</p>
      </div>
      
      <!-- Cancelled Payment -->
      <div v-else-if="paymentStatus === PaymentStatus.CANCELLED">
        <h2 class="mb-10">{{ langContent.payment_cancelled }}</h2>
        <p class="mb-6">{{ langContent.cancelled_message }}</p>
      </div>
      
      <!-- General Payment Error -->
      <div v-else>
        <h2 class="mb-10">{{ langContent.payment_failure }}</h2>
        <p class="mb-6">{{ langContent.payment_error }}</p>
      </div>
      
      <app-button class="mx-auto mt-10" @click="handleNext">
        {{ langContent.retry }}
      </app-button>
    </div>
  </div>
</template>
